import { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { APIResponse } from '../../../../../../lib/commons/api'
import { BulkSheetFilter, FilterResetButton } from '../common'
import { Box, styled, TextField } from '@mui/material'
import { CommentOperatorSelect } from './CommentOperatorSelect'
import { intl } from '../../../../../../i18n'
import {
  DateVO,
  dateVoService,
} from '../../../../../../domain/value-object/DateVO'
import { AllState } from '../../../../../../store'
import { useSelector } from 'react-redux'
import usePrevious from '../../../../../hooks/usePrevious'
import DateInput from '../../../../../components/editors/input/DateInput'

export type CommentTextFilter = {
  operator: CommentTextFilterOperator
  value: string
}

export type CommentCreatedAtFilter = {
  operator: CommentCreatedAtFilterOperator
  value: string
}

export enum CommentTextFilterOperator {
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
}

export enum CommentCreatedAtFilterOperator {
  EQUALS = 'EQUALS',
  AFTER = 'AFTER',
  BEFORE = 'BEFORE',
}

type CommentFilterModel = {
  commentText?: CommentTextFilter
  commentCreatedAt?: CommentCreatedAtFilter
}

type Props = CustomFilterProps & {
  fetch: (
    t?: CommentTextFilter,
    d?: CommentCreatedAtFilter
  ) => Promise<APIResponse>
}

const buildCommentFilterModel = (
  textOperator: CommentTextFilterOperator,
  textValue: string,
  dateOperator: CommentCreatedAtFilterOperator,
  dateValue: DateVO | undefined
): CommentFilterModel | null => {
  const textActive = !!textValue
  const dateActive = !!dateValue
  if (!textActive && !dateActive) return null
  return {
    commentText: textActive
      ? { operator: textOperator, value: textValue }
      : undefined,
    commentCreatedAt: dateActive
      ? {
          operator: dateOperator,
          value: dateValue ? dateVoService.format(dateValue) : '',
        }
      : undefined,
  }
}

export const ServerSideCommentFilter = ({
  model,
  onModelChange,
  api,
  fetch: fetchFn,
}: Props) => {
  const hasRequiredSaveData = useSelector<AllState>(
    state => state.hasRequiredSaveData.hasRequiredSaveData
  )
  const prevHasRequiredSaveData = usePrevious(hasRequiredSaveData)

  // Derive state from model (single source of truth)
  const commentModel = model as CommentFilterModel | null
  const textOperator =
    commentModel?.commentText?.operator ?? CommentTextFilterOperator.CONTAINS
  const textValue = commentModel?.commentText?.value ?? ''
  const dateOperator =
    commentModel?.commentCreatedAt?.operator ??
    CommentCreatedAtFilterOperator.AFTER
  const rawDateValue = commentModel?.commentCreatedAt?.value
  const dateValue = rawDateValue
    ? dateVoService.construct(rawDateValue)
    : undefined

  const textActive = !!textValue
  const dateActive = !!dateValue

  // UI-local state for async filtering
  const [loading, isLoading] = useState(false)
  const [filteredIds, setFilteredIds] = useState<Set<string> | undefined>(
    undefined
  )

  const addedRowIds = useRef<string[]>()
  useEffect(() => {
    if (addedRowIds.current) return
    addedRowIds.current = []
  }, [])

  const fetchFilteredIds = async (
    t?: CommentTextFilter,
    d?: CommentCreatedAtFilter
  ) => {
    const response = await fetchFn(t, d)
    setFilteredIds(new Set(response.json))
  }

  const search = useMemo(
    () =>
      _.debounce(async (t?: CommentTextFilter, d?: CommentCreatedAtFilter) => {
        if (!t && !d) {
          setFilteredIds(undefined)
          return
        }
        try {
          isLoading(true)
          await fetchFilteredIds(t, d)
        } finally {
          isLoading(false)
        }
      }, 300),
    []
  )

  // When criteria changes (model changes), trigger debounced search
  const modelKey = JSON.stringify(model)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- modelKey は model の値比較用。model 自体は毎レンダーで新規オブジェクトになるため deps に入れると毎回発火する。search は useMemo で安定
  useEffect(() => {
    search(commentModel?.commentText, commentModel?.commentCreatedAt)
  }, [modelKey])

  // When filteredIds changes, trigger re-filtering
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api は AG Grid から渡される安定参照。filteredIds 変更時のみ再フィルタリングをトリガーする
  useEffect(() => {
    api.onFilterChanged()
  }, [filteredIds])

  // Re-fetch after save
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hasRequiredSaveData の変化のみをトリガーとし、その時点の最新 model/fetchFilteredIds を読む
  useEffect(() => {
    const isAfterSavedOrRefreshed =
      prevHasRequiredSaveData && !hasRequiredSaveData
    if (isAfterSavedOrRefreshed && (textActive || dateActive)) {
      fetchFilteredIds(
        commentModel?.commentText,
        commentModel?.commentCreatedAt
      ).then(() => {
        addedRowIds.current = []
      })
    }
  }, [hasRequiredSaveData])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.added) {
        if (addedRowIds.current && params.node.id) {
          addedRowIds.current.push(params.node.id)
        }
        return true
      }
      if (
        addedRowIds.current &&
        params.node.id &&
        addedRowIds.current.includes(params.node.id)
      ) {
        return true
      }
      if (!filteredIds) return true
      return params.node.id ? filteredIds.has(params.node.id) : false
    },

    getModelAsString() {
      if (!model) return ''
      const dateString = dateActive
        ? `${
            dateOperator === CommentCreatedAtFilterOperator.EQUALS
              ? '='
              : dateOperator === CommentCreatedAtFilterOperator.AFTER
                ? '≥'
                : dateOperator === CommentCreatedAtFilterOperator.BEFORE
                  ? '≤'
                  : ''
          } ${dateValue && dateVoService.format(dateValue, 'YYYY/MM/DD')}`
        : ''
      return [dateString, textValue].filter(v => !!v).join(',')
    },
  })

  return (
    <BulkSheetFilter>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <FilterRow>
          <CommentOperatorSelect
            id={'comment-date-filter-operator'}
            value={dateOperator}
            options={Object.values(CommentCreatedAtFilterOperator)}
            onChange={v =>
              onModelChange(
                buildCommentFilterModel(textOperator, textValue, v, dateValue)
              )
            }
            getOptionLabel={v => {
              const msg = (postfix: string) =>
                intl.formatMessage({
                  id: `bulksheet.filter.dateFilter.operator.${postfix}`,
                })
              switch (v) {
                case CommentCreatedAtFilterOperator.EQUALS:
                  return msg('equal')
                case CommentCreatedAtFilterOperator.AFTER:
                  return msg('greaterThanEqual')
                case CommentCreatedAtFilterOperator.BEFORE:
                  return msg('lessThanEqual')
                default:
                  return ''
              }
            }}
          />
          <Box sx={{ width: '120px', alignItems: 'center' }}>
            <DateInput
              value={dateValue}
              onChange={v =>
                onModelChange(
                  buildCommentFilterModel(
                    textOperator,
                    textValue,
                    dateOperator,
                    v
                  )
                )
              }
            />
          </Box>
          <FilterResetButton
            onClick={() =>
              onModelChange(
                buildCommentFilterModel(
                  textOperator,
                  textValue,
                  CommentCreatedAtFilterOperator.AFTER,
                  undefined
                )
              )
            }
          />
        </FilterRow>
        <FilterRow>
          <CommentOperatorSelect
            id={'comment-text-filter-operator'}
            value={textOperator}
            options={Object.values(CommentTextFilterOperator)}
            onChange={v =>
              onModelChange(
                buildCommentFilterModel(v, textValue, dateOperator, dateValue)
              )
            }
            getOptionLabel={v => {
              const msg = (postfix: string) =>
                intl.formatMessage({
                  id: `bulksheet.filter.textFilter.operator.${postfix}`,
                })
              switch (v) {
                case CommentTextFilterOperator.CONTAINS:
                  return msg('contain')
                case CommentTextFilterOperator.NOT_CONTAINS:
                  return msg('notContain')
                default:
                  return ''
              }
            }}
          />
          <TextField
            variant="outlined"
            size="small"
            value={textValue}
            onChange={e =>
              onModelChange(
                buildCommentFilterModel(
                  textOperator,
                  e.target.value,
                  dateOperator,
                  dateValue
                )
              )
            }
            sx={{
              width: '120px',
              '& .MuiOutlinedInput-root': {
                '& .MuiInputBase-input': {
                  padding: '3px',
                },
              },
            }}
          />
          <FilterResetButton
            onClick={() =>
              onModelChange(
                buildCommentFilterModel(
                  CommentTextFilterOperator.CONTAINS,
                  '',
                  dateOperator,
                  dateValue
                )
              )
            }
          />
        </FilterRow>
      </Box>
    </BulkSheetFilter>
  )
}

const FilterRow = styled(Box)({
  width: '100%',
  padding: '3px 0',
  display: 'flex',
})
